import {
  computePlacement,
  deviceOrientationToQuaternion,
  QuaternionStabilizer,
  YawRecenter,
  type DeviceOrientationAngles,
  type PlacementConfig,
  type PoseFrame,
} from "@visorxr/core";

export interface GyroPoseParams {
  angles: DeviceOrientationAngles;
  /** Current screen orientation, degrees (0 portrait, 90/-90 landscape). */
  screenOrientationDeg: number;
  placement: PlacementConfig;
  stabilizer: QuaternionStabilizer;
  /** Re-references heading to the user's initial gaze so the sign starts dead ahead. */
  recenter: YawRecenter;
  /** Seconds since the previous frame. */
  dt: number;
}

/**
 * Pure 3DoF pose composition for the gyro backend: the camera sits at the origin (no positional
 * tracking) and is oriented by the stabilized, gaze-recentered device orientation; the signage is
 * placed per the descriptor. Kept free of DOM events so it is unit-tested directly.
 */
export function composeGyroPoseFrame(params: GyroPoseParams): PoseFrame {
  const rawOrientation = deviceOrientationToQuaternion(params.angles, params.screenOrientationDeg);
  const recentered = params.recenter.apply(rawOrientation);
  const orientation = params.stabilizer.filter(recentered, params.dt);

  return {
    camera: { position: { x: 0, y: 0, z: 0 }, orientation },
    object: computePlacement(params.placement),
    trackingState: "tracking",
  };
}
